import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SessionsTable from '@/components/ui/SessionsTable';
import { faComments, faSpinner } from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import type { ChatSession } from '@/models';

interface UserSessionsListProps {
  userId: string;
  userName: string;
}

export default function UserSessionsList({ userId, userName }: UserSessionsListProps) {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchSessions();
  }, [userId]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await APIRoutes.listChatSessions({ userID: userId });
      setSessions(response.sessions);
      setTotal(response.total);
    } catch (err) {
      console.error('Error loading user sessions:', err);
      setError('Fehler beim Laden der Chat-Sitzungen');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Lade Chat-Sitzungen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-red-600 dark:text-red-400">{error}</p>
        <button
          onClick={fetchSessions}
          className="mt-3 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="text-center py-8">
        <FontAwesomeIcon icon={faComments} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500 dark:text-gray-400 mb-2 font-medium">
          Keine Chat-Sitzungen gefunden
        </p>
        <p className="text-gray-400 dark:text-gray-500 text-sm">
          {userName} hat noch keine Chat-Sitzungen erstellt.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Chat-Sitzungen ({total})
        </h3>
      </div>

      <SessionsTable sessions={sessions} showUserColumn={false} />
    </div>
  );
}
